import { useCallback, useMemo, useState } from 'react';

import {
  ComputableProgressInfo,
  ProgressCallback,
  UnknownProgressInfo,
  UploadClient,
  UploadcareFile,
} from '@uploadcare/upload-client';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { AiOutlineUser } from 'react-icons/ai';
import { cn } from 'utils/helpers';
import useModal from 'ui/partials/Modal/useModal';
import Button from 'ui/components/common/Button';
import { toastError, toastSuccess, toastWarning } from 'ui/partials/Toast';

import Progress from './Progress';

interface RejectedFile extends File {
  errors: {
    code: string;
    message: string;
  }[];
}

interface PROPS {
  onSubmit: (item: any) => void;
  removeFileSizeLimit?: boolean;
  defaultImage?: string | null;
  isLoading?: boolean;
}

const sizePerFileLimit = {
  size: 1024 * 1024 * 10, // 10MB
  message: 'One of your files is larger than 10 MB',
};

const Uploader: React.FC<PROPS> = ({
  onSubmit,
  removeFileSizeLimit = false,
  defaultImage,
  isLoading = false,
}) => {
  const { hideModal } = useModal();
  const [isUploading, setUploading] = useState(false);
  const [progressCount, setProgressCount] = useState(0);
  const [fileUploaded, setFileUploaded] = useState<UploadcareFile | null>(null);
  const [removedPhoto, setRemovedPhoto] = useState(false);

  const client = useMemo(
    () =>
      new UploadClient({
        publicKey: '48af1cd2dc896bb801cb',
      }),
    [],
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: RejectedFile[]) => {
      const onProgress: ProgressCallback<ComputableProgressInfo> = ({
        value,
      }) => {
        const loadingProgress = Math.ceil(value * 100);
        setProgressCount(loadingProgress);
      };
      if (!acceptedFiles.length && rejectedFiles.length) {
        if (rejectedFiles[0].errors[0].code === 'file-too-large') {
          return toastError(sizePerFileLimit.message);
        }
        return toastError(rejectedFiles[0].errors[0].message);
      }

      setUploading(true);

      const res = await client.uploadFileGroup(acceptedFiles, {
        onProgress: onProgress as ProgressCallback<
          ComputableProgressInfo | UnknownProgressInfo
        >,
      });
      setUploading(false);

      setFileUploaded(res.files[0]);
      setRemovedPhoto(false);

      if (rejectedFiles.length) {
        return toastWarning(
          `${acceptedFiles.length} files uploaded. ${rejectedFiles.length} files failed to upload`,
        );
      }

      return toastSuccess('Files uploaded successfully');
    },
    [client],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDrop as unknown as <T extends File>(
      acceptedFiles: T[],
      fileRejections: FileRejection[],
      event: DropEvent,
    ) => void,
    noClick: isUploading,
    noKeyboard: isUploading,
    noDrag: isUploading,
    maxSize: removeFileSizeLimit ? 0 : sizePerFileLimit.size,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg'],
    },
    maxFiles: 1,
  });

  const photoUrl = useMemo(() => {
    if (removedPhoto) return null;

    if (fileUploaded) return `${fileUploaded.cdnUrl}-/preview/60x60/`;

    if (defaultImage) return defaultImage;

    return null;
  }, [defaultImage, fileUploaded, removedPhoto]);

  const buttonLabel = useMemo(() => {
    if (isUploading) {
      return (
        <div className="h-[60px] rounded bg-disabled p-4">
          <Progress loading={isUploading} progressCount={progressCount} />{' '}
          Uploading
        </div>
      );
    }

    return (
      <button
        type="button"
        className={cn('rounded-full', 'bg-content', !photoUrl && 'p-5')}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={photoUrl}
            className="h-[60px] w-[60px] rounded-full"
          />
        ) : (
          <AiOutlineUser className="h-5 w-5 text-fill-tertiary" />
        )}
      </button>
    );
  }, [isUploading, progressCount, photoUrl]);

  const handleRemovePhoto = () => {
    setFileUploaded(null);
    setRemovedPhoto(true);
  };

  return (
    <>
      <div className="pt-12 text-center _modal-content-body">
        <div {...getRootProps()} className="flex flex-col items-center">
          <input {...getInputProps()} />
          {buttonLabel}
          <p className="mt-[18px] cursor-pointer text-content leading-none">
            Or drag image here
          </p>
        </div>
        {photoUrl && (
          <button
            onClick={handleRemovePhoto}
            type="button"
            className="mt-2 text-sm font-semibold text-blue"
          >
            REMOVE PHOTO
          </button>
        )}
      </div>
      <div className="flex gap-x-3 justify-end pt-[22px]">
        <Button
          onClick={() => onSubmit(fileUploaded?.cdnUrl || '')}
          disabled={!fileUploaded || isLoading}
          label="SUBMIT"
          intent="primary"
          size="sm"
        />
        <Button
          onClick={hideModal}
          intent="tertiary"
          size="sm"
          label="CANCEL"
        />
      </div>
    </>
  );
};

export default Uploader;
